import { upsertReviewBaseFingerprintExtension } from '@lifeomic/phc-web-toolkit/src/util/contentFingerprint';
import { IMPRESSION_CITATION_SYSTEM } from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/AITools/ImpressionEditor/ImpressionItem';
import { PAGE_NUMBER_EXTENSION_URL } from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/AITools/AttachmentArbiter/Attachment';

export const DATASET_TAG_SYSTEM = 'http://lifeomic.com/fhir/dataset';
export const IMPRESSION_TYPE_SYSTEM =
  'http://lifeomic.com/fhir/fountain-life-clinical-impression-type';
export const IMPRESSION_TEMPLATE_TYPE_CODE = 'templated-report';
export const IMPRESSION_AI_GENERATED_EXTENSION =
  'https://lifeomic.com/fhir/fountain-life-ai-generated';
export const ZORI_CHAT_SOURCE_POST_EXTENSION =
  'https://lifeomic.com/fhir/zori-chat-source-post-id';
export const IMPRESSION_CITATION_ID_SYSTEM =
  'http://lifeomic.com/fhir/fountain-life-citation-id';

type ZoriChatReportAttachmentInput = {
  externalId?: string | null;
  fileName?: string | null;
  subType?: string | null;
};

type BuildZoriChatReportInput = {
  impressionId: string;
  patientId: string;
  projectId: string;
  postId: string;
  message: string;
  metadata?: string | Record<string, unknown> | null;
  attachments?: ZoriChatReportAttachmentInput[];
  exportedAt?: Date;
};

export const buildZoriChatReportTitle = (exportedAt: Date): string =>
  `Zori Chat Export - ${exportedAt.toISOString().slice(0, 10)}`;

const parseMetadata = (
  metadata: BuildZoriChatReportInput['metadata'],
): Record<string, unknown> => {
  if (!metadata) {
    return {};
  }
  if (typeof metadata !== 'string') {
    return metadata;
  }
  try {
    const parsed = JSON.parse(metadata);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch {
    return {};
  }
};

const parsePositiveInteger = (value: string | null) => {
  if (!value || !/^\d+$/.test(value)) {
    return undefined;
  }
  const parsed = Number.parseInt(value, 10);
  return parsed > 0 ? parsed : undefined;
};

const withCitationIdentifier = (
  reference: fhir3.Reference,
  value: string,
): fhir3.Reference => ({
  ...reference,
  identifier: {
    system: IMPRESSION_CITATION_ID_SYSTEM,
    value,
  },
});

const buildReferenceFromCitation = (
  citationId: string,
  rawReference: string,
): fhir3.Reference | undefined => {
  const trimmedReference = rawReference.trim();
  if (!trimmedReference) {
    return undefined;
  }

  if (trimmedReference.startsWith('s3://')) {
    return withCitationIdentifier({ reference: trimmedReference }, citationId);
  }

  if (trimmedReference.includes('|')) {
    const [, , resourceId] = trimmedReference.split('|');
    return resourceId
      ? withCitationIdentifier(
          { reference: `Observation/${resourceId}` },
          citationId,
        )
      : undefined;
  }

  if (trimmedReference.startsWith('DocumentReference/')) {
    const [reference, query] = trimmedReference.split('?');
    const page = parsePositiveInteger(
      query ? new URLSearchParams(query).get('page') : null,
    );
    return withCitationIdentifier(
      {
        reference,
        extension: page
          ? [{ url: PAGE_NUMBER_EXTENSION_URL, valueInteger: page }]
          : undefined,
      },
      citationId,
    );
  }

  if (trimmedReference.startsWith('QuestionnaireResponse/')) {
    const [reference] = trimmedReference.split('?');
    return withCitationIdentifier({ reference }, citationId);
  }

  if (trimmedReference.startsWith('Observation/')) {
    const [reference] = trimmedReference.split('?');
    return withCitationIdentifier({ reference }, citationId);
  }

  return undefined;
};

const buildReferenceFromAttachment = (
  attachment: ZoriChatReportAttachmentInput,
  index: number,
): fhir3.Reference | undefined => {
  const externalId = attachment.externalId?.trim();
  if (!externalId) {
    return undefined;
  }

  if (externalId.startsWith('DocumentReference/')) {
    return withCitationIdentifier(
      { reference: externalId, display: attachment.fileName ?? undefined },
      `dr-attachment-${index}`,
    );
  }

  if (/document[-_ ]?reference/i.test(attachment.subType ?? '')) {
    return withCitationIdentifier(
      {
        reference: `DocumentReference/${externalId}`,
        display: attachment.fileName ?? undefined,
      },
      `dr-attachment-${index}`,
    );
  }

  return undefined;
};

export const buildZoriChatReportReferences = ({
  metadata,
  attachments = [],
}: Pick<BuildZoriChatReportInput, 'metadata' | 'attachments'>):
  | fhir3.Reference[]
  | undefined => {
  const metadataReferences = Object.entries(parseMetadata(metadata))
    .map(([citationId, reference]) =>
      typeof reference === 'string'
        ? buildReferenceFromCitation(citationId, reference)
        : undefined,
    )
    .filter((reference): reference is fhir3.Reference => !!reference);

  const attachmentReferences = attachments
    .map(buildReferenceFromAttachment)
    .filter((reference): reference is fhir3.Reference => !!reference);

  const seenReferences = new Set<string>();
  const references = [...metadataReferences, ...attachmentReferences].filter(
    reference => {
      const key = `${reference.identifier?.value ?? ''}|${reference.reference}`;
      if (seenReferences.has(key)) {
        return false;
      }
      seenReferences.add(key);
      return true;
    },
  );

  return references.length > 0 ? references : undefined;
};

export const buildZoriChatReportResource = ({
  impressionId,
  patientId,
  projectId,
  message,
  exportedAt = new Date(),
  extension,
  references,
}: BuildZoriChatReportInput & {
  extension: fhir3.ClinicalImpression['extension'];
  references?: fhir3.Reference[];
}): fhir3.ClinicalImpression & { id: string } => ({
  resourceType: 'ClinicalImpression',
  id: impressionId,
  status: 'draft',
  date: exportedAt.toISOString(),
  description: buildZoriChatReportTitle(exportedAt),
  summary: message,
  subject: {
    reference: `Patient/${patientId}`,
  },
  code: {
    text: 'Zori Report',
    coding: [
      {
        system: IMPRESSION_TYPE_SYSTEM,
        code: 'general',
        display: 'General',
      },
    ],
  },
  meta: {
    tag: [
      {
        system: DATASET_TAG_SYSTEM,
        code: projectId,
      },
      {
        system: IMPRESSION_TYPE_SYSTEM,
        code: IMPRESSION_TEMPLATE_TYPE_CODE,
      },
    ],
  },
  investigation: references?.length
    ? [
        {
          code: { text: IMPRESSION_CITATION_SYSTEM },
          item: references,
        },
      ]
    : undefined,
  extension,
});

export const buildZoriChatReportClinicalImpression = async (
  input: BuildZoriChatReportInput,
): Promise<fhir3.ClinicalImpression & { id: string }> => {
  const extension = await upsertReviewBaseFingerprintExtension(
    [
      {
        url: IMPRESSION_AI_GENERATED_EXTENSION,
        valueBoolean: true,
      },
      {
        url: ZORI_CHAT_SOURCE_POST_EXTENSION,
        valueString: input.postId,
      },
    ],
    input.message,
  );

  const references = buildZoriChatReportReferences(input);

  return buildZoriChatReportResource({
    ...input,
    extension,
    references,
  });
};
